import * as React from "react";
import "./reports.css";
import { Button, Icon } from "semantic-ui-react";
import { Outlet, redirect, useFetcher, useLoaderData } from "react-router-dom";
import { Nullable } from "../../../globals/types";
import { reportRegistry } from "../show/report-registry";
import { AppState } from "../../../stores/app-mobx";
import { mkConfig, generateCsv, download } from "export-to-csv";
import moment from "moment";
import { Constants } from "../../../globals/constants";
import { RootStore } from "../../../stores/root-store";
import { getLabel, getLabeledProperties } from "../../../reflection/reflection";
import { LandfillSiteProject } from "../../../stores/project/project";
import { updateUninitializedCreationStatus } from "../../../stores/area/creation-area-update";

type LoadResult = {
    selectedReport: string;
    rootStore: RootStore;
}

type ColumnHeader = {
    key: string;
    displayLabel: string;
}

export async function loader(request: Request, appStore: AppState, rootStore: RootStore): Promise<LoadResult> {
    const url = new URL(request.url);
    // E.g. /reports/report-beanspruchungs-projekte => ["", "reports", "report-beanspruchungs-projekte"]
    const splitPath = url.pathname.split("/");
    appStore.setShowMap(false);
    const selectedReport = splitPath.slice(-1)[0];
    return { selectedReport, rootStore }
}

export async function action(request: Request) {
    const formData = await request.formData();
    const reportKey: Nullable<string> = formData.get("reportKey") as Nullable<string>;

    if (reportKey !== null) {
        return redirect(reportKey);
    }

    return null;
}

async function csvExport(rootStore: RootStore) {
    const rootStoreSession = rootStore.newSession();
    await rootStoreSession.municipalityStore.loadAll();
    await rootStoreSession.contactStore.loadAll();
    await rootStoreSession.userStore.loadAll();
    await rootStoreSession.activityLogEntryStore.loadAll();
    await rootStoreSession.compensationStore.loadAll();
    await rootStoreSession.areaStore.loadAll();
    const projects = (await rootStoreSession.projectStore.loadAll()).filter(p => p.typeKey !== Constants.projectTypeRecultivation);
    await updateUninitializedCreationStatus(rootStoreSession);
    const columnHeaders: ColumnHeader[] = [];
    const projectObjects = [];

    for (const proj of projects) {
        let projectObject = {};
        const props = getLabeledProperties(proj);

        for (const prop of props) {
            let propFinal = prop;
            const propSuffixed = prop + proj.typeKey;
            const label = getLabel(proj, prop);

            if (label !== undefined) {
                let columnHeader = columnHeaders.find(e => e.key === prop);

                if (columnHeader === undefined) {
                    columnHeaders.push({ key: prop, displayLabel: label });
                } else if (columnHeader.displayLabel !== label) {
                    propFinal = propSuffixed;
                    columnHeader = columnHeaders.find(e => e.key === propSuffixed);

                    if (columnHeader === undefined) {
                        columnHeaders.push({ key: propSuffixed, displayLabel: label });
                    }
                }

                projectObject[propFinal] = proj[prop];
            }
        }

        if (proj.typeKey === Constants.projectTypeLandfillSite) {
            const recultivationProj = (proj as LandfillSiteProject).recultivationProject;
            const props = getLabeledProperties(recultivationProj);

            for (const prop of props) {
                // If the recultivation project has a property with the same name,
                // only take the property from the landfill site project
                if (!(prop in projectObject)) {
                    let propFinal = prop;
                    const propSuffixed = prop + recultivationProj.typeKey;
                    const label = getLabel(recultivationProj, prop);

                    if (label !== undefined) {
                        let columnHeader = columnHeaders.find(e => e.key === prop);

                        if (columnHeader === undefined) {
                            columnHeaders.push({ key: prop, displayLabel: label });
                        } else if (columnHeader.displayLabel !== label) {
                            propFinal = propSuffixed;
                            columnHeader = columnHeaders.find(e => e.key === propSuffixed);

                            if (columnHeader === undefined) {
                                columnHeaders.push({ key: propSuffixed, displayLabel: label });
                            }
                        }

                        projectObject[propFinal] = recultivationProj[prop];
                    }
                }
            }
        }

        projectObject = Object.fromEntries(
            Object.entries(projectObject).filter(([_, value]) => value !== undefined)
        );

        projectObjects.push(projectObject);
    }

    const csvConfig = mkConfig({
        fieldSeparator: ";",
        columnHeaders: columnHeaders,
        useKeysAsHeaders: false,
        boolDisplay: { true: "ja", false: "nein" },
        filename: `FFF-Projekte-${moment().format("DD-MM-YYYY_HH-mm-ss")}`,
    });
    const csv = generateCsv(csvConfig)(projectObjects);
    download(csvConfig)(csv);
}

export default function Reports(): JSX.Element {
    const fetcher = useFetcher();
    const { selectedReport, rootStore } = useLoaderData() as LoadResult;

    return (
        <div className="reports">
            <div className="header">
                <h1>Reporting</h1>
                <div>
                    <Button color="green" type="button"
                            onClick={async () => csvExport(rootStore)}>
                        <Icon name='cloud download'/>
                        Projekte (CSV)
                    </Button>
                </div>
                <fetcher.Form method="post" className="selection">
                    <select name="reportKey" className="ui selection dropdown">
                        <option key={0} disabled={true} selected={selectedReport === "reports"}>
                            -- Report auswählen --
                        </option>
                        {reportRegistry.map(r => (
                            <option key={r.key} value={r.key} selected={r.key === selectedReport}>
                                {r.title}
                            </option>
                        ))}
                    </select>
                    <Button type="submit">Anzeigen</Button>
                </fetcher.Form>
            </div>
            <Outlet/>
        </div>
    );
}
